import { ReturnLocation } from 'types/policy';
import { isValidEmailInput, isValidPhoneInput } from 'utils/settingsInput';

type ReturnLocationInput = Partial<ReturnLocation> & Record<string, any>;

export const RETURN_LOCATION_FIELD_LIMITS = {
  id: 128,
  label: 27,
  street: 35,
  street2: 35,
  city: 200,
  state: 200,
  zip: 20,
  country: 100,
  country_alpha2: 2,
  phone: 20,
  email: 50,
} as const;

const COUNTRIES_REQUIRING_STATE = new Set([
  'AU',
  'CA',
  'CN',
  'ID',
  'MX',
  'MY',
  'TH',
  'US',
  'VN',
]);

export type ReturnLocationValidationErrors = Partial<
  Record<
    | 'label'
    | 'street'
    | 'street2'
    | 'city'
    | 'state'
    | 'zip'
    | 'country'
    | 'country_alpha2'
    | 'phone'
    | 'email',
    string
  >
>;

const stringValue = (...values: unknown[]) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const stringified = String(value).trim();
    if (stringified) return stringified;
  }

  return '';
};

const limitedStringValue = (limit: number, ...values: unknown[]) =>
  stringValue(...values).slice(0, limit);

const getLengthError = (value: unknown, limit: number, label: string) => {
  if (stringValue(value).length <= limit) return undefined;
  return `${label} must be ${limit} characters or fewer.`;
};

export const sanitizeCountryCodeInput = (value: string) =>
  value
    .replace(/[^a-z]/gi, '')
    .slice(0, RETURN_LOCATION_FIELD_LIMITS.country_alpha2)
    .toUpperCase();

export const getReturnLocationValidationErrors = (
  location: ReturnLocationInput,
): ReturnLocationValidationErrors => {
  const errors: ReturnLocationValidationErrors = {};
  const fields = [
    ['label', 'Location name'],
    ['street', 'Street address'],
    ['street2', 'Address line 2'],
    ['city', 'City'],
    ['state', 'State'],
    ['zip', 'ZIP or postal code'],
    ['country', 'Country'],
    ['phone', 'Phone number'],
    ['email', 'Email address'],
  ] as const;

  fields.forEach(([field, label]) => {
    const error = getLengthError(
      location[field],
      RETURN_LOCATION_FIELD_LIMITS[field],
      label,
    );
    if (error) errors[field] = error;
  });

  if (!stringValue(location.street)) {
    errors.street = 'Street address is required.';
  }
  if (!stringValue(location.city)) errors.city = 'City is required.';
  if (!stringValue(location.zip)) {
    errors.zip = 'ZIP or postal code is required.';
  }
  if (!stringValue(location.country)) {
    errors.country = 'Country is required.';
  }

  const countryCode = stringValue(location.country_alpha2).toUpperCase();
  if (!/^[A-Z]{2}$/.test(countryCode)) {
    errors.country_alpha2 = 'Enter a valid 2-letter country code.';
  }
  if (
    COUNTRIES_REQUIRING_STATE.has(countryCode) &&
    !stringValue(location.state)
  ) {
    errors.state = 'State is required for this country.';
  }

  const phone = stringValue(location.phone);
  if (!errors.phone && phone && !isValidPhoneInput(phone)) {
    errors.phone = 'Enter a valid phone number using numbers and + ( ) . -.';
  }

  const email = stringValue(location.email);
  if (!errors.email && email && !isValidEmailInput(email)) {
    errors.email = 'Enter a valid email address.';
  }

  return errors;
};

export const normalizeReturnLocationForSave = (
  location: ReturnLocationInput,
  fallbackId = '',
): ReturnLocation => {
  const shopifyLocationId = stringValue(
    location.shopifyLocationId,
    location.shopify_location_id,
  );
  const source = location.source === 'SHOPIFY' ? 'SHOPIFY' : 'MANUAL';
  const country = stringValue(
    location.country,
    location.countryName,
    location.country_name,
  );
  const countryAlpha2 = stringValue(
    location.country_alpha2,
    location.countryAlpha2,
    location.countryCodeV2,
    location.countryCode,
    location.country_code,
    country.length === 2 ? country : '',
  );
  const normalized: ReturnLocation = {
    id: limitedStringValue(
      RETURN_LOCATION_FIELD_LIMITS.id,
      location.id,
      fallbackId,
    ),
    label: stringValue(location.label, location.name),
    street: stringValue(
      location.street,
      location.line_1,
      location.street1,
      location.address1,
      location.address_1,
      location.line1,
    ),
    city: stringValue(location.city),
    state: stringValue(
      location.state,
      location.province,
      location.provinceCode,
      location.province_code,
    ),
    zip: stringValue(location.zip, location.postalCode, location.postal_code),
    country: country || countryAlpha2,
    country_alpha2: countryAlpha2.toUpperCase(),
    phone: stringValue(
      location.phone,
      location.contactPhone,
      location.contact_phone,
    ),
    email: stringValue(
      location.email,
      location.contactEmail,
      location.contact_email,
    ),
    isDefault: Boolean(
      location.isDefault ?? location.is_default ?? location.default,
    ),
    source,
  };

  if (source === 'SHOPIFY' && shopifyLocationId) {
    normalized.shopifyLocationId = shopifyLocationId.slice(
      0,
      RETURN_LOCATION_FIELD_LIMITS.id,
    );
  }

  const street2 = stringValue(
    location.line_2,
    location.street2,
    location.address2,
    location.address_2,
    location.line2,
  );
  if (street2) normalized.street2 = street2;

  return normalized;
};
